import React from 'react';

import type { AiChatReference } from '~/components/AiChat/types';
import { CliveAvatar } from '~/components/Clive/CliveAvatar';
import { Text } from '~/components/core';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { CommunicationIcon, DocumentIcon, NoteIcon } from '~/components/icons';
import { EmailIcon, PhoneIcon, PhysicalMailIcon, SmsIcon, VendorIcon } from '~/components/icons/notifications';

/**
 * DocumentReferencePreview
 *
 * Business logic component for document/note/communication references.
 * Handles icon selection, summary display, and formatting of reference metadata.
 */

const ICONS_MAP = {
  document: DocumentIcon,
  communication_phone_call: PhoneIcon,
  communication_email: EmailIcon,
  communication_physical_mail: PhysicalMailIcon,
  communication_sms: SmsIcon,
  communication_vendor: VendorIcon,
  communication: CommunicationIcon,
  note: NoteIcon,
};

const getIcon = (reference: AiChatReference) => {
  const { type, sub_type } = reference;
  let iconKey = type;
  if (type === 'communication') {
    iconKey = `${type}_${sub_type}`;
  }

  const Icon = ICONS_MAP[iconKey as keyof typeof ICONS_MAP];
  return Icon ? <Icon className="inline h-[16px] w-[16px]" iconColor="black" /> : null;
};

const getTypeLabel = (reference: AiChatReference): string => {
  if (reference.type === 'communication') {
    return (reference.sub_type || 'communication').replace('_', ' ');
  }
  return reference.type || 'document';
};

interface DocumentReferencePreviewProps {
  reference: AiChatReference;
}

export const DocumentReferencePreview: React.FC<DocumentReferencePreviewProps> = ({ reference }) => {
  const displaySummary = reference.ai_summary || reference.summary;
  const isAiSummary = !!reference.ai_summary;

  return (
    <div className="max-w-[400px] rounded-lg p-12">
      <div className="flex flex-col items-start gap-4">
        <div className="flex w-full min-w-0 flex-row items-center gap-4">
          <div className="mt-4">{getIcon(reference)}</div>
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="min-w-0 flex-1 truncate">
            {reference.name || 'Unnamed'}
          </Text>
          {isAiSummary && (
            <Tooltip title="AI-generated summary">
              <span className="flex-shrink-0">
                <CliveAvatar mood={CliveAvatar.MOOD.HAPPY} size={14} />
              </span>
            </Tooltip>
          )}
        </div>
        <div className="w-full min-w-0 flex-1">
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="mb-4 capitalize">
            {reference.document_title || getTypeLabel(reference)}
          </Text>
          {displaySummary && (
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="break-word whitespace-normal">
              {displaySummary}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

// Export helper function for use in trigger button
export { getIcon };
